/**
 * Analytics identity wiring (PIX-553).
 *
 * Thin adapter: takes the user payload and the active board, and pushes them
 * at the facade. All the actual derivation lives in eventProperties.js, which
 * is pure and separately tested - this file is deliberately logic-free, the
 * same way sentry.js only wires errorFilters.js.
 *
 * IDENTIFIER: the user-info payload keys the account on `uuid`. There is no
 * `id` field on it, so anything reading `user.id` here would silently
 * identify nobody - the exact failure mode this project exists to eliminate.
 */
import { watch } from 'vue';

import { useActiveBoardStore } from '@/stores/activeBoard';
import { analytics } from './analytics.js';
import { GROUPS, PROPS, SURFACES } from './events.js';
import { decideIdentityAction, IDENTITY_ACTIONS } from './identitySwitch.js';
import {
  deriveBoardContext,
  deriveOrganisation,
  deriveSuperProperties,
} from './eventProperties.js';

/**
 * Deploy environment label attached to every event (PIX-603).
 *
 * Reads its own variable rather than borrowing Sentry's. `VITE_SENTRY_ENVIRONMENT`
 * is set in none of the three deploy workflows, so this used to fall through to a
 * hardcoded `'production'` and every staging event claimed to come from
 * production - a property that was quietly wrong in one of our two projects.
 *
 * The fallback is `'development'` deliberately. A workflow that forgets to set
 * the variable then reads as an obvious anomaly rather than silently
 * impersonating production, and local dev - the one place it is legitimately
 * unset - emits nothing anyway, because no project key is configured there.
 */
function environmentLabel() {
  return import.meta.env.VITE_APP_ENVIRONMENT || 'development';
}

/**
 * Associate subsequent events with a user and refresh their context.
 * Safe to call repeatedly - every user fetch re-derives, which is how a
 * subscription change mid-session stops tagging events with stale status.
 */
export function identifyAnalyticsUser(user, { activeBoard = null } = {}) {
  const distinctId = user?.uuid;
  if (!distinctId) return;

  // Guarded in both directions (PIX-605). The panel seam does the same: on a
  // shared school machine a student and a teacher can each be authenticated,
  // and whichever fetch resolves last claims the browser's single distinct id.
  // Severing first is what stops the two of them merging into one person -
  // guarding only the panel side would leave the same corruption reachable
  // from this one. Reset costs a new session, so it happens only on a genuine
  // change of person, never on the re-identify that every user fetch performs.
  const action = decideIdentityAction({
    currentIdentifiedId: analytics.identifiedDistinctId(),
    nextId: distinctId,
  });
  if (action === IDENTITY_ACTIONS.RESET_THEN_IDENTIFY) {
    analytics.reset();
  }
  if (action !== IDENTITY_ACTIONS.NOOP) {
    analytics.identify(distinctId);
  }

  analytics.registerSuperProperties(
    deriveSuperProperties({
      user,
      activeBoardUuid: activeBoard?.uuid ?? null,
      activeBoardName: activeBoard?.name ?? null,
      environment: environmentLabel(),
    })
  );

  // Group association makes per-school rollups possible without a second
  // instrumentation pass over the teacher surfaces.
  const organisation = deriveOrganisation(user);
  if (organisation?.uuid) {
    analytics.associateGroup(GROUPS.ORGANISATION, organisation.uuid, {
      name: organisation.name,
    });
  }
}

/**
 * Clear identity and accumulated context. Call on logout.
 *
 * Re-seeds the anonymous baseline afterwards: reset drops every super
 * property, and without this the events a logged-out visitor emits next
 * would carry no authentication state at all rather than `false`.
 */
export function resetAnalyticsIdentity() {
  analytics.reset();
  analytics.registerSuperProperties(anonymousBaseline());
}

/** Super properties that apply before anyone has identified. */
export function anonymousBaseline() {
  return deriveSuperProperties({ environment: environmentLabel() });
}

/**
 * Last known student board, remembered here so that returning to the student
 * app from the panel can restore it. Navigation alone does not touch the board
 * store, so the watcher below never fires on the way back - without this the
 * board would stay null until the student next changed it.
 */
let studentBoardContext = { uuid: null, name: null };

/** The surface currently registered, defaulting to student before any
 *  navigation has been observed. */
function currentSurface() {
  return analytics.getSuperProperties()[PROPS.SURFACE] ?? SURFACES.STUDENT;
}

/**
 * Update just the board context. Split from the user path because the active
 * board changes on its own (board switcher, hydration) and because
 * activeBoard.js deliberately keeps itself free of alias-only imports so it
 * stays testable in plain node - so the dependency points this way, not back.
 *
 * Re-applied against the surface we are actually on, so a board change while
 * the teacher is in the panel records nulls rather than their student board.
 */
export function setAnalyticsBoardContext({ uuid = null, name = null } = {}) {
  studentBoardContext = { uuid, name };
  applyBoardContextForSurface(currentSurface());
}

/**
 * Reconcile board context with a surface: cleared on the panel, restored to the
 * student's own board on the way back out. The single writer of board context,
 * called from the router binding on every navigation - because the surface
 * changes without the board store changing, so the watcher below never fires.
 */
export function applyBoardContextForSurface(surface) {
  analytics.registerSuperProperties(
    deriveBoardContext({ surface, ...studentBoardContext })
  );
}

/**
 * Keep board context in step with the store for the life of the app. Call
 * once after Pinia is installed.
 *
 * Board is the breakdown almost every question in the PRD is sliced by, and
 * it changes independently of the user (board switcher, hydration on load).
 * Watching it here rather than calling out from the store keeps activeBoard.js
 * dependency-free, which is what lets it be tested in plain node.
 */
export function bindAnalyticsBoardContext() {
  const boards = useActiveBoardStore();
  watch(
    () => [boards.activeBoard, boards.activeBoardName],
    ([uuid, name]) => setAnalyticsBoardContext({ uuid, name }),
    { immediate: true }
  );
}
